import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ChevronDown, Check, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BillingCreatePage() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [formData, setFormData] = useState({
    quote_id: '',
    order_no: '',
    job_sheet_description: '',
    invoice_number: '',
    afm_name: '',
    total_amount: '',
    server_name: '',
    service_period: ''
  });
  const [loading, setLoading] = useState(false);
  
  // Quote search states
  const [quoteSearch, setQuoteSearch] = useState('');
  const [showQuoteDropdown, setShowQuoteDropdown] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchQuotes();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowQuoteDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API}/quotes`);
      setQuotes(response.data);
    } catch (error) {
      toast.error('Failed to load quotes');
    }
  };

  // Filter quotes based on search
  const filteredQuotes = quotes.filter(quote => 
    quote.quote_no?.toLowerCase().includes(quoteSearch.toLowerCase())
  );

  const handleQuoteSelect = (quote) => {
    setSelectedQuote(quote);
    setFormData({ ...formData, quote_id: quote.id });
    setQuoteSearch(quote.quote_no);
    setShowQuoteDropdown(false);
  };

  const clearQuoteSelection = () => {
    setSelectedQuote(null);
    setFormData({ ...formData, quote_id: '' });
    setQuoteSearch('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.quote_id) {
      toast.error('Please select a quote');
      return;
    }
    
    if (!formData.order_no) {
      toast.error('Please enter Order No');
      return;
    }
    
    setLoading(true);

    try {
      await axios.post(`${API}/billing`, {
        quote_id: formData.quote_id,
        order_no: formData.order_no,
        invoice_number: formData.invoice_number,
        invoice_format: 'standard', // Default to standard format
        total_amount: parseFloat(formData.total_amount),
        server_name: formData.server_name,
        service_period: formData.service_period,
        job_sheet_description: formData.job_sheet_description || '',
        afm_name: formData.afm_name
      });
      toast.success('Billing created successfully');
      navigate('/billing');
    } catch (error) {
      // Handle Pydantic validation errors (array of objects) or string errors
      const errorDetail = error.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        // Pydantic validation error - extract message from first error
        const firstError = errorDetail[0];
        toast.error(firstError?.msg || 'Validation error');
      } else if (typeof errorDetail === 'string') {
        toast.error(errorDetail);
      } else if (typeof errorDetail === 'object' && errorDetail?.msg) {
        toast.error(errorDetail.msg);
      } else {
        toast.error('Failed to create billing');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sidebar>
      <div className="space-y-6 max-w-2xl" data-testid="billing-create-page">
        <div>
          <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Create Billing
          </h1>
          <p className="text-muted-foreground mt-2">
            Create a new billing entry for a quote
          </p>
        </div>

        <Card className="p-6 glass-card">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="quote">Select Quote *</Label>
              <div className="relative" ref={dropdownRef}>
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="Type or select a quote number..."
                    value={quoteSearch}
                    onChange={(e) => {
                      setQuoteSearch(e.target.value);
                      setShowQuoteDropdown(true);
                      // Try to find exact match
                      const exactMatch = quotes.find(q => q.quote_no === e.target.value);
                      if (exactMatch) {
                        setSelectedQuote(exactMatch);
                        setFormData({ ...formData, quote_id: exactMatch.id });
                      } else {
                        setSelectedQuote(null);
                        setFormData({ ...formData, quote_id: '' });
                      }
                    }}
                    onFocus={() => setShowQuoteDropdown(true)}
                    className={`pr-16 ${selectedQuote ? 'border-green-500' : ''}`}
                    data-testid="quote-search-input"
                  />
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
                    {selectedQuote && (
                      <button
                        type="button"
                        onClick={clearQuoteSelection}
                        className="p-1 hover:bg-gray-100 rounded"
                      >
                        <X className="h-4 w-4 text-gray-400" />
                      </button>
                    )}
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
                
                {showQuoteDropdown && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                    {filteredQuotes.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-gray-500">
                        No quotes found
                      </div>
                    ) : (
                      filteredQuotes.map((quote) => (
                        <div
                          key={quote.id}
                          className={`px-4 py-2 cursor-pointer hover:bg-gray-100 flex items-center justify-between ${
                            selectedQuote?.id === quote.id ? 'bg-[#DC143C] text-white hover:bg-[#b91130]' : ''
                          }`}
                          onClick={() => handleQuoteSelect(quote)}
                        >
                          <span>
                            {quote.quote_no} - ₹{quote.amount_with_management_fee?.toLocaleString('en-IN')}
                          </span>
                          {selectedQuote?.id === quote.id && (
                            <Check className="h-4 w-4" />
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
                
                {selectedQuote && (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <Check className="h-3 w-3" />
                    Selected: {selectedQuote.quote_no} - ₹{selectedQuote.amount_with_management_fee?.toLocaleString('en-IN')}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="order_no">Order No *</Label>
              <Input
                id="order_no"
                placeholder="Enter Order Number"
                value={formData.order_no}
                onChange={(e) => setFormData({ ...formData, order_no: e.target.value })}
                required
                data-testid="order-no-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="invoice_number">JMS No *</Label>
              <Input
                id="invoice_number"
                placeholder="JMS-2026-001"
                value={formData.invoice_number}
                onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                required
                data-testid="invoice-number-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="afm_name">AFM Name *</Label>
              <Input
                id="afm_name"
                placeholder="Enter AFM name"
                value={formData.afm_name}
                onChange={(e) => setFormData({ ...formData, afm_name: e.target.value })}
                required
                data-testid="afm-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="total_amount">Total Amount (₹) *</Label>
              <Input
                id="total_amount"
                type="number"
                step="0.01"
                placeholder="50000"
                value={formData.total_amount}
                onChange={(e) => setFormData({ ...formData, total_amount: e.target.value })}
                required
                data-testid="amount-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="server_name">Server Name *</Label>
              <Input
                id="server_name"
                placeholder="Enter server name"
                value={formData.server_name}
                onChange={(e) => setFormData({ ...formData, server_name: e.target.value })}
                required
                data-testid="server-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="service_period">Service Period *</Label>
              <Input
                id="service_period"
                placeholder="Enter service period (e.g., Jan 2026 - Mar 2026)"
                value={formData.service_period}
                onChange={(e) => setFormData({ ...formData, service_period: e.target.value })}
                required
                data-testid="service-period-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="job_sheet_description">Job Sheet Description</Label>
              <Textarea
                id="job_sheet_description"
                placeholder="Detailed description of work completed..."
                value={formData.job_sheet_description}
                onChange={(e) => setFormData({ ...formData, job_sheet_description: e.target.value })}
                rows={5}
                data-testid="description-input"
              />
            </div>

            <div className="flex gap-3">
              <Button type="submit" disabled={loading} data-testid="submit-button">
                {loading ? 'Creating...' : 'Create Billing'}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/billing')}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </Sidebar>
  );
}
